import React, {
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useGet } from '@/query/fetchHooks';
import { FroalaTextareaView } from '@/components/Froala/FroalaTextareaView';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import useState from 'react-usestateref';
import { axios } from '@/axios';
import { Button, PersonAvatar } from '@teo/components';
import { useNavigate } from 'react-router-dom';
import i18n from 'i18next';
import { isEmpty, toPercent, newUid, isObject } from '@/util';
import { config } from '@/config';
import { Subject, BehaviorSubject, skip } from 'rxjs';
import { XAPIService } from '@/services/xapi.service.js';
import { BoxIoService } from '@/components/Froala/widgets/teo_box/BoxIoService';

// ⬇️ NEW: Course language context
import { CourseLanguageContext } from '@/components/Contexts/CourseLanguageContext';

const AUTOSAVE_DELAY = 20; //20s

const sameId = (a: any, b: any) =>
  a != null && b != null && String(a) === String(b);

const widgetSignature = (widgets: any) => {
  if (!widgets) return '';

  return Object.values(widgets)
    .map((widget: any) => widget?.state?.uid ?? widget?.uid ?? '')
    .sort()
    .join('|');
};

export const LessonView = ({
  node,
  status,
  course,
  setSubmitFn,
  student,
  timeDisabled,
  isTeacher,
  page = 0,
  setNrOfPages = () => {},
  setLoadeContent,
  nodeGetData,
  content,
  showAnswers = false,
  pageBreak = true,
  onSubmit = () => {},
  videoLink = false,
  setIsLoaded,
}: {
  node: any;
  status?: any;
  course?: any;
  setSubmitFn?: any;
  student?: any;
  timeDisabled?: boolean;
  isTeacher?: boolean;
  page?: number;
  setNrOfPages?: any;
  setLoadeContent?: any;
  nodeGetData?: any;
  content?: string;
  showAnswers?: boolean;
  pageBreak?: boolean;
  onSubmit?: any;
  videoLink?: boolean;
  setIsLoaded?: any;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auth, setAuth } = useContext(AuthContext);

  const lessonId = node?.lessonId;
  const hasStudentId = !!student?.id;
  const hasResultScope = hasStudentId && lessonId != null && course?.id != null;

  const lessonContentKey = [
    lessonId ?? node?.id ?? node?.uid ?? 'unknown-lesson',
    node?.version ?? 'no-version',
  ].join(':');

  const lessonScopeKey = [
    lessonId ?? node?.id ?? node?.uid ?? 'unknown-lesson',
    course?.id ?? 'unknown-course',
    student?.id ?? 'no-student',
  ].join(':');

  let doAutosave = hasResultScope;
  if (
    status &&
    (status.status === 'completed' ||
      status.status === 'corrected' ||
      status.status === 'submitted')
  ) {
    doAutosave = false;
  }

  const [splittedContent, setSplittedContent] = useState<any>(null);
  const [isLoaded, setMyIsLoaded] = useState(false);

  useEffect(() => {
    setSplittedContent(null);
    setMyIsLoaded(false);
    setNrOfPages(0);
  }, [lessonContentKey]);

  // ⬇️ NEW: course language state (defaults to course.lang or i18n.language)
  const [courseLang, setCourseLang, courseLangRef] = useState<string>(
    String(course?.lang ?? i18n.language)
  );

  const queryParams = new URLSearchParams(window.location.search);
  const lpgroupId = queryParams.get('lpgroupId');

  const modifiedWidgets = useRef<Set<string>>(new Set());
  const subscriptions = useRef<any[]>([]);
  const saveTimer: any = useRef(null);

  // Guardrail: current render scope. Old async callbacks compare against this.
  const activeScopeRef = useRef<string>(lessonScopeKey);
  const activeContentScopeRef = useRef<string>(lessonContentKey);
  const previousScopeRef = useRef<string | null>(lessonScopeKey);

  activeScopeRef.current = lessonScopeKey;
  activeContentScopeRef.current = lessonContentKey;

  const lastWidgetSignatureRef = useRef<string>('');
  const handlerMap = useRef(
    new WeakMap<HTMLAnchorElement, (e: MouseEvent) => void>()
  );

  const unsubscribeAll = useCallback(() => {
    for (const subscription of subscriptions.current) {
      subscription.unsubscribe();
    }
    subscriptions.current = [];
  }, []);

  const lastNrOfPagesRef = useRef<number | null>(null);

  useEffect(() => {
    setCourseLang(String(course?.lang ?? i18n.language));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.lang]);

  useEffect(() => {
    return () => {
      clearTimeout(saveTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!isTeacher && course?.courseType == 'assessment') {
      setIsLoaded?.(false);
    }
  }, [isTeacher, course]);

  useEffect(() => {
    if (!isLoaded) return;

    const links = document.querySelectorAll<HTMLAnchorElement>('a.fr-file');

    links.forEach((link) => {
      const href0 = link.getAttribute('href');
      if (!href0) return;

      // Normalize once
      const normalizedHref = href0
        .replace(/froala%252F/g, 'froala/')
        .replace(/froala%2F/g, 'froala/');

      if (normalizedHref !== href0) link.setAttribute('href', normalizedHref);

      const href = normalizedHref;

      // Only special-case these
      const isForceDownload = href.endsWith('.drawio') || href.endsWith('.xml');
      if (!isForceDownload) return;

      // Avoid reattaching
      if (handlerMap.current.has(link)) return;

      // Filename: strip query string just in case
      const lastPart = href.split('/').pop() ?? '';
      const filename = decodeURIComponent(lastPart.split('?')[0]);

      link.setAttribute('download', filename);

      const handler = async (e: MouseEvent) => {
        e.preventDefault();
        try {
          // NOTE: this requires S3 CORS if cross-origin
          const res = await fetch(href, { mode: 'cors' });
          if (!res.ok) throw new Error(`Download failed: ${res.status}`);

          const blob = await res.blob();
          const url = URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } catch (err) {
          console.error('Download error:', err);
        }
      };

      handlerMap.current.set(link, handler);
      link.addEventListener('click', handler);
    });

    return () => {
      // remove only handlers we added
      links.forEach((link) => {
        const h = handlerMap.current.get(link);
        if (h) {
          link.removeEventListener('click', h);
          handlerMap.current.delete(link);
        }
      });
    };
  }, [isLoaded]);

  useEffect(() => {
    return () => {
      unsubscribeAll();
    };
  }, []);

  useGet(nodeGetData ? nodeGetData : node?.getData, {
    enabled:
      content === undefined && (nodeGetData ? !!nodeGetData : !!node?.getData),
    onSuccess: (result: any) => {
      if (activeContentScopeRef.current !== lessonContentKey) return;

      let splitted_html: Array<string>;
      if (pageBreak) {
        splitted_html = result?.data.split(
          /<div class="pageBreak[\s\S]*?<\/div>/gms
        );
      } else {
        splitted_html = [result?.data];
      }

      if (lastNrOfPagesRef.current !== splitted_html.length) {
        lastNrOfPagesRef.current = splitted_html.length;
        setNrOfPages(splitted_html.length);
      }

      setSplittedContent((prev: any) => {
        if (
          Array.isArray(prev) &&
          prev.length === splitted_html.length &&
          prev.every((x, i) => x === splitted_html[i])
        ) {
          return prev;
        }

        return splitted_html;
      });
    },
  });

  useEffect(() => {
    if (content === undefined) return;

    const splittedHtml = pageBreak
      ? content.split(/<div class="pageBreak[\s\S]*?<\/div>/gms)
      : [content];

    setNrOfPages(splittedHtml.length);
    setSplittedContent(splittedHtml);
  }, [content, lessonContentKey, pageBreak, setNrOfPages]);

  const [widgets, setWidgets, widgetsRef] = useState<any>({});
  const [result, setResult, resultRef] = useState<any>({});
  const [rerenderView, setRerenderView] = useState<any>(newUid(20));

  const backendIsCorrected = hasStudentId && status?.status === 'corrected';
  const backendIsSubmitted = hasStudentId && status?.status === 'submitted';

  const [resultsLoaded, setResultsLoaded] = useState<boolean>(!hasStudentId);

  // Guardrail: when the lesson/course/student scope changes, do not keep stale
  // widget subscriptions, autosave timers, or result objects from the previous scope.
  useEffect(() => {
    const previousScope = previousScopeRef.current;

    if (previousScope && previousScope !== lessonScopeKey) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;

      modifiedWidgets.current.clear();
      unsubscribeAll();

      setWidgets({});
      setResult({});
      setResultsLoaded(!hasStudentId);
      lastWidgetSignatureRef.current = '';
      setRerenderView(newUid(20));
    }

    previousScopeRef.current = lessonScopeKey;

    const networkService = BoxIoService.getInstance();
    networkService.resetAll?.();
  }, [lessonScopeKey, hasStudentId]);

  const releaseDateReached =
    isTeacher ||
    !backendIsCorrected ||
    !result?.releaseDate ||
    new Date(result.releaseDate).getTime() <= Date.now();

  const effectiveStatus =
    backendIsCorrected && !releaseDateReached ? 'submitted' : status?.status;

  const isCorrected = hasStudentId && effectiveStatus === 'corrected';
  const isSubmitted = hasStudentId && effectiveStatus === 'submitted';

  const widgetsForView = useMemo(() => {
    if (!result?.widgets) return result?.widgets;

    // Teachers always see the real corrected state
    if (isTeacher) return result.widgets;

    // Normal released corrected result: show full correction
    if (effectiveStatus === 'corrected') return result.widgets;

    // Submitted OR corrected-but-not-yet-released:
    // keep answers/state, hide only correction
    const cloned: any = {};
    for (const [key, widget] of Object.entries(result.widgets)) {
      cloned[key] = { ...(widget as any) };
      delete cloned[key].correction;
    }
    return cloned;
  }, [result?.widgets, isTeacher, effectiveStatus]);

  const followupPath = `/${i18n.language}/follow-up/${auth?.user?.activeOrganisationId}/resultaat/${course?.id}/${student?.id}/${result?.lessonId}`;
  const lpgroupPath = `/${i18n.language}/follow-up/${auth?.user?.activeOrganisationId}/resultaat/${course?.id}/${student?.id}/${result?.lessonId}?lpgroupId=${lpgroupId}`;
  const learnPath = `/${i18n.language}/learn/${auth?.user?.activeOrganisationId}/resultaat/${course?.id}/${student?.id}/${result?.lessonId}`;

  let showContent = !hasStudentId || !isTeacher || effectiveStatus;

  //hide content for assessments
  if (
    (effectiveStatus === 'corrected' || effectiveStatus === 'submitted') &&
    !isTeacher &&
    course?.courseType === 'assessment'
  ) {
    showContent = false;
  }

  const getLatestResult = (data: any) =>
    data?.length > 0 ? data[data.length - 1] : {};

  const sameResult = (a: any, b: any) =>
    a?._id === b?._id &&
    a?.updatedAt === b?.updatedAt &&
    a?.releaseDate === b?.releaseDate;

  useGet<any>(`${config.teodoc}/corrections/list2`, {
    lessonId,
    courseId: course?.id,
    userId: student?.id,

    enabled: hasResultScope && backendIsCorrected,

    retry: false,
    refetchOnWindowFocus: false,

    // Only works if your useGet forwards this to axios config,
    // not if it only treats unknown fields as query params.
    timeout: 3000,
    onSuccess: (response: any) => {
      if (activeScopeRef.current !== lessonScopeKey) return;

      const nextResult = getLatestResult(response?.data);

      setResultsLoaded(true);
      setLoadeContent?.(true);

      if (sameResult(resultRef.current, nextResult)) {
        return;
      }

      setResult(nextResult);
      lastWidgetSignatureRef.current = '';
      setRerenderView(newUid(20));
    },
    onError: () => {
      if (activeScopeRef.current !== lessonScopeKey) return;

      setResultsLoaded(true);
      setLoadeContent?.(true);
      setResult({});
    },
  });

  useGet<any>(`${config?.teodoc}/results/list2`, {
    lessonId,
    submitted: status?.status === 'submitted' ? 'true' : undefined,
    courseId: course?.id,
    userId: student?.id,

    enabled: hasResultScope && !backendIsCorrected,

    retry: false,
    refetchOnWindowFocus: false,
    timeout: 3000,
    onSuccess: (response: any) => {
      if (activeScopeRef.current !== lessonScopeKey) return;

      const nextResult = getLatestResult(response?.data);

      setResultsLoaded(true);
      setLoadeContent?.(true);

      if (sameResult(resultRef.current, nextResult)) {
        return;
      }

      setResult(nextResult);
      lastWidgetSignatureRef.current = '';
      setRerenderView(newUid(20));
    },
    onError: () => {
      if (activeScopeRef.current !== lessonScopeKey) return;

      setResultsLoaded(true);
      setLoadeContent?.(true);
      setResult({});
    },
  });

  const getSubmit = async (
    submit = false,
    force = false,
    triggerWidget: any = undefined
  ) => {
    const submitScopeKey = lessonScopeKey;

    // Guardrail: never save without a concrete backend scope.
    if (!hasResultScope) return;

    // Guardrail: ignore autosaves/onSave callbacks from a previous lesson.
    if (activeScopeRef.current !== submitScopeKey) return;

    const currentResult = resultRef.current;

    const currentResultMatches =
      currentResult &&
      isObject(currentResult) &&
      sameId(currentResult.lessonId, lessonId) &&
      sameId(currentResult.courseId, course?.id) &&
      sameId(currentResult.userId, student?.id);

    let toSubmit = currentResultMatches ? { ...currentResult } : null;

    if (!toSubmit) {
      toSubmit = {
        userId: student?.id,
        coursecontentId: course?.coursecontentId,
        courseId: course?.id,
        lessonId: lessonId,
        // Pin to whichever lesson content version the student is actually
        // looking at (status.version, when a lessonstatuses row already
        // exists) rather than the live/current node's version — otherwise
        // an in-progress attempt's pin gets silently overwritten on first
        // save, see the version-tagging comment below.
        version: status?.version ?? node?.version,
        versionId: status?.version ?? node?.version,
        releaseDate: new Date(),
        lessonName: node?.name,
        studentName: student?.username,
        courseName: course?.name,
        className: course?.courseName,
        widgets: {},
      };
    }

    let allSubmitted = true;
    for (const widget of Object.values(widgetsRef.current)) {
      if ((widget as any)?.answerFn) {
        const answer = await (widget as any)?.answerFn?.();

        if (activeScopeRef.current !== submitScopeKey) return;

        if (!answer.submitted) {
          allSubmitted = false;
          break;
        }
      }
    }

    toSubmit.allSubmitted = allSubmitted;
    if (allSubmitted) submit = true;

    const incremental = !!toSubmit._id && !submit;

    toSubmit.userId = student?.id;
    toSubmit.courseId = course?.id;
    toSubmit.coursecontentId = course?.coursecontentId;
    toSubmit.lessonId = lessonId;
    // Never clobber an already-pinned version with the live/current node's
    // version: once an attempt has a version (from an existing result, or
    // just set above when this result was first created), keep it — that's
    // the whole point of pinning a student to the content they started on.
    toSubmit.version = toSubmit.version ?? status?.version ?? node?.version;

    const requiresManualCorrection = Object.values(widgetsRef.current).some(
      (w: any) =>
        w?.state?.type === 'OpenVraag' || w?.state?.type === 'FileUpload'
    );

    const autoCorrect =
      submit &&
      !requiresManualCorrection &&
      (course.correctionType === 'auto' ||
        course.correctionType === 'immediate' ||
        course.correctionType === 'autofeedback');

    if (submit) {
      toSubmit.submitted = true;
      toSubmit.submitDate = new Date();
      if (autoCorrect) {
        toSubmit.autoCorrected = true;
        toSubmit.feedback = t('Automatisch verbeterd');
        toSubmit.releaseDate = new Date();
      }
    } else {
      toSubmit.submitted = false;
    }

    toSubmit.score = 0;
    toSubmit.maxScore = 0;

    const widgetsToSave = incremental
      ? [...modifiedWidgets.current]
      : Object.keys(widgetsRef.current);

    clearTimeout(saveTimer.current);
    saveTimer.current = null;
    modifiedWidgets.current.clear();

    // Preserve existing widgets during incremental saves.
    // This avoids accidentally replacing all widgets with only the modified subset.
    toSubmit.widgets =
      incremental && isObject(toSubmit.widgets) ? { ...toSubmit.widgets } : {};

    const incompleteWidgets = [];
    for (const key of widgetsToSave) {
      const widget = widgetsRef.current[key];
      if (!widget?.state?.noSubmit) {
        const correctioneWidgets = (await widget?.answerFn?.()) || {};

        if (activeScopeRef.current !== submitScopeKey) return;

        incompleteWidgets.push(correctioneWidgets);
      }
    }

    for (const key of widgetsToSave) {
      const widget = widgetsRef.current[key];
      if (!widget || widget.state.noSubmit) continue;

      const immediateFeedback =
        widget.state.immediateFeedback ||
        course?.correctionType === 'autofeedback';

      if (!toSubmit.widgets) toSubmit.widgets = {};

      try {
        toSubmit.widgets[key] = Object.assign(
          {},
          {
            state: widget.state,
            answer: (await widget?.answerFn?.()) || {},
          }
        );

        if (activeScopeRef.current !== submitScopeKey) return;

        if (
          autoCorrect ||
          (immediateFeedback && toSubmit.widgets[key].answer.submitted)
        ) {
          toSubmit.widgets[key].correction = await widget?.correctionFn?.();

          if (activeScopeRef.current !== submitScopeKey) return;
        } else if (incremental && currentResultMatches) {
          // Keep existing correction if this is only an autosave of another widget.
          const existingCorrection = currentResult?.widgets?.[key]?.correction;
          if (existingCorrection) {
            toSubmit.widgets[key].correction = existingCorrection;
          }
        }

        if (submit) {
          if (toSubmit.widgets[key].answer.incomplete && !force)
            return {
              status: 'incomplete',
              widget: toSubmit.widgets[key],
              uid: key,
              incompleteWidgets: incompleteWidgets
                .filter((item) => item.incomplete === true)
                .map((item) => item.uid),
            };

          toSubmit.widgets[key].answered = true;

          if (autoCorrect && submit) {
            toSubmit.widgets[key].answer.incomplete = false;
            toSubmit.widgets[key].answer.submitted = true;
          }
        } else if (
          incremental &&
          currentResultMatches &&
          currentResult?.widgets?.[key]?.answered
        ) {
          toSubmit.widgets[key].answered = currentResult.widgets[key].answered;
        }
      } catch (e) {
        console.error(e);
      }
    }

    toSubmit.score = 0;
    toSubmit.maxScore = 0;

    for (const savedWidget of Object.values(toSubmit.widgets || {})) {
      toSubmit.maxScore += parseFloat((savedWidget as any)?.state?.score || 0);
      toSubmit.score += parseFloat(
        (savedWidget as any)?.correction?.score || 0
      );
    }

    for (const key of Object.keys(toSubmit.widgets)) {
      const widget = widgetsRef.current[key];
      if (!widget) continue;

      const immediateFeedback =
        widget.state.immediateFeedback ||
        course?.correctionType === 'autofeedback';

      const shouldReport =
        (immediateFeedback && triggerWidget?.uid === key) ||
        (submit && !toSubmit.widgets[key].answer.submitted);

      if (XAPIService.getInstance().available() && widget.xApiSubmitFn) {
        try {
          if (widget.xApiSubmitFn && shouldReport) {
            widget?.xApiSubmitFn?.(
              toSubmit.lessonId,
              i18n.language,
              course?.minScore || 0.5
            );
          }
        } catch (e) {
          console.error(e);
        }
      }
    }

    // Final guard before touching the backend.
    if (activeScopeRef.current !== submitScopeKey) return;

    if (
      !sameId(toSubmit.lessonId, lessonId) ||
      !sameId(toSubmit.courseId, course?.id) ||
      !sameId(toSubmit.userId, student?.id)
    ) {
      console.warn('LessonView: refused to submit result for stale scope', {
        toSubmit: {
          lessonId: toSubmit.lessonId,
          courseId: toSubmit.courseId,
          userId: toSubmit.userId,
        },
        current: {
          lessonId,
          courseId: course?.id,
          userId: student?.id,
        },
      });
      return;
    }

    let result;
    if (submit && autoCorrect) {
      result = axios.post(`${config.teodoc}/corrections`, toSubmit);
    } else if (toSubmit._id) {
      result = axios.put(`${config.teodoc}/results/${toSubmit._id}`, toSubmit);
    } else {
      result = axios.post(`${config.teodoc}/results`, toSubmit);
    }

    const newResult = (await result)?.data;

    // Response may arrive after navigating away.
    if (activeScopeRef.current !== submitScopeKey) {
      return newResult;
    }

    onSubmit(newResult);
    setResult(newResult);
    return newResult;
  };

  const getSubmitRef = useRef<any>(getSubmit);
  getSubmitRef.current = getSubmit;

  const handleSetWidgets = useCallback(
    (nextWidgets: any) => {
      if (activeScopeRef.current !== lessonScopeKey) return;

      const nextSignature = widgetSignature(nextWidgets);

      // This is the important loop breaker.
      // If FroalaTextareaView reports the same widget set again, do not trigger
      // another LessonView render.
      if (lastWidgetSignatureRef.current === nextSignature) {
        return;
      }

      lastWidgetSignatureRef.current = nextSignature;

      unsubscribeAll();

      const widgetValues = Object.values(nextWidgets);

      subscriptions.current = [
        ...widgetValues.map((widget: any) =>
          widget.onModified.subscribe(() => {
            if (activeScopeRef.current !== lessonScopeKey) return;

            modifiedWidgets.current.add(widget.state.uid);

            if (doAutosave && !saveTimer.current) {
              const scopeAtScheduleTime = lessonScopeKey;

              saveTimer.current = setTimeout(() => {
                saveTimer.current = null;

                if (activeScopeRef.current !== scopeAtScheduleTime) {
                  return;
                }

                getSubmitRef.current?.();
              }, AUTOSAVE_DELAY * 1000);
            }
          })
        ),

        ...widgetValues.map((widget: any) =>
          widget.onSave.subscribe(({ submit, force, triggerWidget }: any) => {
            if (activeScopeRef.current !== lessonScopeKey) return;

            getSubmitRef.current?.(submit, force, triggerWidget);
          })
        ),
      ];

      setWidgets(nextWidgets);
    },
    [lessonScopeKey, doAutosave, unsubscribeAll]
  );

  useEffect(() => {
    if (!setSubmitFn) return;

    if (!hasResultScope) {
      setSubmitFn(null);
      return;
    }

    if (!widgetsRef.current) return setSubmitFn(null);

    let canSubmit = false;
    for (const key of Object.keys(widgetsRef.current)) {
      if (!widgetsRef.current[key].state.noSubmit) canSubmit = true;
    }

    if (!canSubmit) return setSubmitFn(null);

    setSubmitFn(getSubmit);
  }, [node, node?.id, widgets, hasResultScope, lessonScopeKey]);

  const handleSetIsLoaded = useCallback(() => {
    setMyIsLoaded(true);
    setIsLoaded?.();
  }, [setIsLoaded]);

  if (!splittedContent || !resultsLoaded) return null;

  return (
    <CourseLanguageContext.Provider value={{ courseLang, setCourseLang }}>
      <>
        {node?.timed === 'limited' && !timeDisabled && (
          <div className="rounded-md bg-error-01 py-3 px-3 text-sm text-error-05">
            {t('pages.course_landing.time_message')}
          </div>
        )}

        {effectiveStatus === 'corrected' && (
          <div className="my-5 rounded-lg border p-4">
            <div className="text-sm font-semibold">
              {isTeacher
                ? t('widgets.type.student_completed_lesson')
                : t('widgets.type.title_completed_lesson')}
            </div>

            {(isTeacher || course?.courseType !== 'assessment') && (
              <>
                <div className="py-2 text-sm font-normal text-[#3257fc]">
                  {t('widgets.type.final_score')}
                  &nbsp;
                  <span className="font-semibold">
                    {toPercent(
                      status?.score || result?.score,
                      status?.maxScore || result?.maxScore
                    )}
                    %
                  </span>
                </div>

                <div className="my-3 rounded-lg bg-[#1e1e200d] py-2 px-3">
                  <div className="text-xs font-normal uppercase text-[#919193]">
                    {t('widgets.type.feedback')}
                    &nbsp;
                    {result?.teacherName}
                  </div>
                  <div className="flex items-center py-2">
                    <PersonAvatar
                      inline
                      id={student?.id}
                      name={student?.username}
                      imageSrc={student?.picture}
                    ></PersonAvatar>
                    <div className="pl-2 text-sm font-medium">
                      {student.username}
                    </div>
                  </div>
                  <div
                    className="text-sm font-normal text-[#4d4d50]"
                    dangerouslySetInnerHTML={{ __html: result?.feedback }}
                  ></div>
                </div>
                <Button
                  className="my-2"
                  variant="outline"
                  size="md"
                  onClick={() =>
                    navigate(
                      lpgroupId
                        ? lpgroupPath
                        : isTeacher
                        ? `${followupPath}?view_les=true`
                        : learnPath
                    )
                  }
                >
                  {isTeacher
                    ? t('widgets.type.view_his_answers_button')
                    : t('widgets.type.view_your_answers_button')}
                </Button>
              </>
            )}

            {!isTeacher && course?.courseType == 'assessment' && (
              <>
                <div className="my-3 rounded-lg bg-[#1e1e200d] py-2 px-3">
                  <b>{t('widgets.type.result_hidden')}</b>
                </div>
              </>
            )}
          </div>
        )}

        {effectiveStatus === 'submitted' && (
          <div className="my-5 rounded-lg border p-4">
            <div className="text-sm font-semibold">
              {isTeacher
                ? t('widgets.type.title_completed_lesson_teacher')
                : t('widgets.type.title_completed_lesson')}
            </div>

            <div className="my-3 rounded-lg bg-[#1e1e200d] py-2 px-3">
              <div className="text-xs font-normal uppercase text-[#919193]">
                {t('widgets.type.feedback')}
              </div>
              <div className="flex items-center py-2">
                <PersonAvatar
                  inline
                  id={student?.id}
                  name={student?.username}
                  imageSrc={student?.picture}
                ></PersonAvatar>
                <div className="pl-2 text-sm font-medium">
                  {student?.username}
                </div>
              </div>
              <div className="text-sm font-normal text-[#4d4d50]">
                {isTeacher
                  ? t('widgets.type.submitted_feedback_teacher')
                  : t('widgets.type.submitted_feedback')}
              </div>
            </div>
          </div>
        )}

        {isTeacher && status?.status === 'stored' && (
          <div className="my-5 rounded-lg border bg-[#1e1e200d] p-4">
            <div className="text-sm font-semibold">
              {t('widgets.type.stored')}
            </div>
          </div>
        )}

        {isTeacher &&
          (!status?.status ||
            status?.status == 'started' ||
            status?.status == 'completed') && (
            <div className="my-5 rounded-lg border bg-[#1e1e200d] p-4">
              <div className="text-sm font-semibold">
                {t('widgets.type.not_started')}
              </div>
            </div>
          )}

        {resultsLoaded && showContent && (
          <FroalaTextareaView
            correctionType={course?.correctionType}
            courseStatus={course?.courseStatus}
            showAnswers={showAnswers}
            key={`${lessonContentKey}:${lessonScopeKey}:${rerenderView}`}
            value={splittedContent}
            correction={widgetsForView}
            page={page}
            lesson={node}
            isCorrected={isCorrected}
            setWidgets={handleSetWidgets}
            setIsLoaded={handleSetIsLoaded}
          />
        )}
      </>
    </CourseLanguageContext.Provider>
  );
};
